import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Menu, X } from 'lucide-react';
import { LeftRail, type LeftRailTab } from './LeftRail';
import { ThreadList } from './ThreadList';
import { ActiveThread } from './ActiveThread';
import { useFactoryRuns } from './hooks/useFactoryRuns';
import type { FactoryRun } from '@/lib/types';

// Composes the three panes. Owns the selected-run state so both the list
// and the active thread agree. Hoists useFactoryRuns to one subscription
// (instead of duplicating it inside ThreadList + ActiveThread).
//
// Mobile behavior (≤768px):
//   - left rail collapses behind a hamburger
//   - thread list + active thread stack: list on top, thread on bottom
//     when a thread is selected, or list full-screen when not.
//   - Responsive stub only — not a priority this session.
export function SystemsShell() {
  const runs = useFactoryRuns();
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LeftRailTab>('threads');
  const [mobileRailOpen, setMobileRailOpen] = useState(false);

  // Auto-select the first pinned run (or first all run) on mount so the
  // shell doesn't feel empty on first visit.
  useEffect(() => {
    if (activeRunId) return;
    const first = runs.pinned[0] ?? runs.all[0] ?? runs.archived[0];
    if (first) setActiveRunId(first.id);
  }, [activeRunId, runs.pinned, runs.all, runs.archived]);

  const selectedRun: FactoryRun | null = useMemo(() => {
    if (!activeRunId) return null;
    return (
      runs.pinned.find((r) => r.id === activeRunId) ??
      runs.all.find((r) => r.id === activeRunId) ??
      runs.archived.find((r) => r.id === activeRunId) ??
      null
    );
  }, [activeRunId, runs.pinned, runs.all, runs.archived]);

  // Tab filter: tab choice narrows the lists passed to ThreadList. Only
  // "threads" shows the full grouped view; "archive" surfaces archived
  // only; "inbox" = pinned only; "notifications" = no-op placeholder.
  const filtered = useMemo(() => {
    if (activeTab === 'inbox') {
      return { ...runs, all: [], archived: [] };
    }
    if (activeTab === 'archive') {
      return { ...runs, pinned: [], all: [] };
    }
    if (activeTab === 'notifications') {
      return { ...runs, pinned: [], all: [], archived: [] };
    }
    return runs;
  }, [activeTab, runs]);

  return (
    <div className="relative flex h-dvh w-full overflow-hidden bg-canvas-bg">
      {/* Desktop left rail */}
      <div className="hidden md:block">
        <LeftRail activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Mobile: hamburger + slide-over rail */}
      <button
        type="button"
        aria-label="Open nav"
        onClick={() => setMobileRailOpen(true)}
        className="fixed left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-xl bg-rail-900 text-rail-200 shadow-glass md:hidden"
      >
        <Menu size={16} />
      </button>
      {mobileRailOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="relative h-full">
            <LeftRail
              activeTab={activeTab}
              onTabChange={(t) => {
                setActiveTab(t);
                setMobileRailOpen(false);
              }}
            />
            <button
              type="button"
              aria-label="Close nav"
              onClick={() => setMobileRailOpen(false)}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-rail-800 text-rail-200"
            >
              <X size={14} />
            </button>
          </div>
          <button
            type="button"
            aria-label="Close nav overlay"
            onClick={() => setMobileRailOpen(false)}
            className="flex-1 bg-ink-900/30"
          />
        </div>
      )}

      {/* Thread list + active thread. On mobile, show only one at a time. */}
      <div
        className={clsx(
          'flex h-full w-full min-w-0 flex-col md:flex-row',
          // Hide list on mobile once a thread is selected.
          selectedRun ? 'md:flex' : 'md:flex',
        )}
      >
        <div
          className={clsx(
            'w-full md:w-auto md:flex-shrink-0',
            // Stack rule: hide thread list on mobile when a thread is
            // selected, to mimic the Slack collapse behavior.
            selectedRun ? 'hidden md:block' : 'block',
          )}
        >
          <ThreadList
            activeRunId={activeRunId}
            onSelectRun={setActiveRunId}
            pinned={filtered.pinned}
            all={filtered.all}
            archived={filtered.archived}
            isMock={runs.isMock}
            loading={runs.loading}
            error={runs.error}
          />
        </div>
        <div
          className={clsx(
            'min-w-0 flex-1',
            selectedRun ? 'block' : 'hidden md:block',
          )}
        >
          <ActiveThread run={selectedRun} />
          {selectedRun && (
            <div className="pointer-events-none fixed inset-x-0 bottom-0 flex justify-center pb-2 md:hidden">
              <button
                type="button"
                onClick={() => setActiveRunId(null)}
                className="pointer-events-auto rounded-full bg-rail-900 px-4 py-1.5 text-xs text-rail-200 shadow-glass"
              >
                ← back to threads
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
